import React from 'react';

import {buildSkillCatalog} from '@embertracking/common';

import CheckIcon from '@mui/icons-material/Check';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import ErrorOutlinedIcon from '@mui/icons-material/ErrorOutlined';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';

// The qualification vocabulary shared by the department app and the hub: the stages a
// skill moves through, and how each is drawn. Lived in embertracking/src/data/utils.js
// until the hub needed to render the same cells; that module now re-exports these so its
// importers are untouched. (The driver/operator checklist lived here too until it became
// a skill-drill section — see ./driverOperator.js and common/skillDrillsDriverOperator.js.)


export const SkillStages = {
    INCOMPLETE: {
        label: 'Incomplete',
        color: 'primary',
        icon: <DoNotDisturbIcon />
    },
    NEEDED: {
        label: 'Needed',
        color: 'needed',
        icon: <ErrorOutlinedIcon />
    },
    SCHEDULED: {
        label: 'Scheduled',
        color: 'inProgress',
        icon: <CalendarMonthIcon />
    },
    INPROGRESS: {
        label: 'In Progress',
        color: 'inProgress',
        icon: <HourglassBottomIcon />
    },
    COMPLETE: {
        label: 'Complete',
        color: 'complete',
        icon: <CheckIcon />
    }
};

// A cell is signed off when an officer set COMPLETE, or when it carries a completedDate
// written before `status` was stored. Same rule as sectionCheckedOff in
// embertracking/src/data/skillDrills.js — they must never disagree, which is why that
// module now imports this one rather than restating the test.
//
// Accepts either shape the matrix stores: a bare status string (the oldest records) or
// the `{status, completedDate, …}` map.
export const isCellSignedOff = cell => {
    const value = typeof cell === 'string' ? {status: cell} : (cell || {});
    return value.status === 'COMPLETE' || (!value.status && Boolean(value.completedDate));
};

// The department's effective skill catalog and the lookups over it. Was
// `embertracking/src/hooks/useSkills.js`, which read the two inputs below from
// SettingsContext; as a plain function of those it serves the hub too, where the
// same settings arrive per-department from the api rather than from a context.
//
// The catalog itself now lives in @embertracking/common (buildSkillCatalog), because the
// api needs the same answers for its expiry-reminder emails and can't import this module
// — it's React. This is the thin re-export the two SPAs already import by name; see
// common/skillCatalog.js for how aliases and renamed paths resolve.
export const buildSkills = ({qualificationAdditionalCategories = [], trainingProvider} = {}) =>
    buildSkillCatalog({qualificationAdditionalCategories, trainingProvider});
